import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Query } from '../op-types';

/**
 * The UNAUTHENTICATED surface: a shared portfolio behind its share id, the
 * fox F12 public score card, and the SEO pre-render export the app's static
 * build is assembled from.
 */
export function createPublicQueries(client: StoxlyClient) {
  return createQueryKeys('public', {
    // ['public', 'detail', shareId, { query }, ...]
    detail: (shareId: string, query?: Query<'publicPortfoliosGetPublicPortfolio'>) => ({
      queryKey: [shareId, { query }],
      queryFn: ({ signal }) =>
        client.publicPortfoliosGetPublicPortfolio({
          params: { shareId },
          queries: query,
          signal,
        }),
      contextQueries: {
        portfolioMetadata: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.publicPortfoliosGetPortfolioMetadata({ params: { shareId }, signal }),
        },
      },
    }),

    // ['public', 'viewer', shareId] — WHO is reading a shared portfolio: `{ access, isSubscribed,
    // hasPlan }` and nothing else. Not a page view (it counts none and computes nothing), so it is
    // the read to POLL after a checkout until the plan activates, instead of re-fetching `detail`.
    viewer: (shareId: string) => ({
      queryKey: [shareId],
      queryFn: ({ signal }) => client.publicPortfoliosGetViewer({ params: { shareId }, signal }),
    }),

    // ['public', 'scores', { query }] — fox F12. The paged index of scored
    // symbols. The page is part of the answer, so it is part of the key.
    scores: (query?: Query<'publicScoresIndex'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.publicScoresIndex({ queries: query, signal }),
    }),

    // ['public', 'scorePage', symbol] — one symbol's public score page.
    scorePage: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.publicScoresScorePage({ params: { symbol }, signal }),
    }),

    // ['public', 'seoExport'] — every pre-renderable page of app.stoxly.ai. The
    // nightly consumer is the Cloudflare postbuild, which fetches it directly
    // rather than through this client (it is a plain Node script with no
    // dependencies); this binding exists so the operation has a typed call site,
    // and so an admin surface can eyeball the corpus without hand-rolling the
    // URL. The payload is large — do not put it behind a component that mounts
    // on a user route.
    seoExport: {
      queryKey: null,
      queryFn: ({ signal }) => client.seoExportExport({ signal }),
    },
  });
}

/**
 * Following a share link (owner ruling R1): the subscription row is written AT THE CLICK, plan or
 * no plan, and both writes answer the viewer body `{ access, isSubscribed, hasPlan }` — the SAME
 * shape `public.viewer(shareId)` serves, so seat the answer straight into that query. A no-plan
 * subscribe answers `{ access: 'viewer', isSubscribed: true, hasPlan: false }`, never an error.
 */
export function createPublicMutations(client: StoxlyClient) {
  return {
    subscribe: {
      mutationFn: (shareId: string) =>
        client.publicPortfoliosSubscribe(undefined, { params: { shareId } }),
    },
    unsubscribe: {
      mutationFn: (shareId: string) =>
        client.publicPortfoliosUnsubscribe(undefined, { params: { shareId } }),
    },
  };
}
